function isArrayOfElements(value) {
    return Array.isArray(value);
}
function arrayOfElements(value) {
    return ( /*#__INLINE__*/isArrayOfElements(value)) ? value : value != null ? [value] : [];
}

/**
 * Checks whether two values are the same. I.e. strictly equal to each other.
 *
 * @typeParam T - A type of values.
 * @param first - First value to compare.
 * @param second - Second value to compare.
 *
 * @returns `true` if `first === second`, or `false` otherwise.
 */
function areTheSame(first, second) {
    return first === second;
}

/**
 * @internal
 */
function isAsyncEvaluator(value) {
    return typeof value === 'function';
}
/**
 * Asynchronously evaluates a value by its recipe.
 *
 * @typeParam TValue - Evaluated value type. This can not be a function.
 * @typeParam TArgs - A type of parameters tuple required for value evaluation.
 * @param recipe - Asynchronous value evaluation recipe.
 * @param args - Parameters required for value evaluation.
 *
 * @returns A promise resolved to the value.
 */
async function asyncByRecipe(recipe, ...args) {
    return ( /*#__INLINE__*/isAsyncEvaluator(recipe)) ? recipe(...args) : recipe;
}
/**
 * Converts asynchronous value recipe to its {@link AsyncRecipe.Evaluator evaluator} function.
 *
 * @typeParam TValue - Evaluated value type. This can not be a function.
 * @typeParam TArgs - A type of parameters tuple required for value evaluation.
 * @param recipe - Asynchronous value recipe to convert.
 *
 * @returns A function asynchronously evaluating the value.
 */
function asyncRecipe(recipe) {
    return ( /*#__INLINE__*/isAsyncEvaluator(recipe))
        ? (async (...args) => recipe(...args))
        : () => Promise.resolve(recipe);
}

/**
 * Creates a provider of the only argument.
 *
 * @param value - A value to return.
 *
 * @returns A function that returns `value`.
 */
function valueProvider(value) {
    return () => value;
}
/**
 * Creates a provider of arguments tuple.
 *
 * The returned tuple should never be modified as it is not cloned.
 *
 * @param values - Values to return.
 *
 * @returns A function that returns `values` tuple.
 */
function valuesProvider(...values) {
    return () => values;
}
/**
 * Creates a provider of cloned arguments tuple.
 *
 * The returned tuple can be modified as it is cloned before the return.
 *
 * @param values - Values to return.
 *
 * @returns A function that returns a clone of `values` tuple.
 */
function valuesCloner(...values) {
    return () => values.slice();
}

/**
 * Creates a provider of lazily evaluated value.
 *
 * The returned function evaluates the value first time it is called. Then it just returns the previously evaluated one.
 * The failed value evaluation will be retried on the next call.
 *
 * @param provider - A no-arg function evaluating the value.
 *
 * @returns A function that returns the value evaluated by `provider`.
 */
function lazyValue(provider) {
    let get;
    const getValue = () => {
        get = lazyValue$recurrent; // Prevent recurrent evaluation
        try {
            const value = provider();
            get = valueProvider(value);
            return value;
        }
        catch (error) {
            get = getValue;
            throw error;
        }
    };
    get = getValue;
    return () => get();
}
function lazyValue$recurrent() {
    throw new TypeError('Recurrent evaluation');
}

/**
 * @internal
 */
function isValueEvaluator(value) {
    return typeof value === 'function';
}
/**
 * Evaluates a value by its recipe.
 *
 * @typeParam TValue - Evaluated value type. This can not be a function.
 * @typeParam TArgs - A type of parameters tuple required for value evaluation.
 * @param recipe - Value evaluation recipe.
 * @param args - Parameters required for value evaluation.
 *
 * @returns Either the value itself, or the one evaluated by the given evaluator recipe.
 */
function valueByRecipe(recipe, ...args) {
    return ( /*#__INLINE__*/isValueEvaluator(recipe)) ? recipe(...args) : recipe;
}
/**
 * Converts a value recipe to its {@link ValueRecipe.Evaluator evaluator} function.
 *
 * @typeParam TValue - Evaluated value type. This can not be a function.
 * @typeParam TArgs - A type of parameters tuple required for value evaluation.
 * @param recipe - Value recipe to convert.
 *
 * @returns Either evaluator itself, or the one evaluating to the given value.
 */
function valueRecipe(recipe) {
    return ( /*#__INLINE__*/isValueEvaluator(recipe)) ? recipe : valueProvider(recipe);
}

function arraysAreEqual(first, second, compareOrFromOrLength, fromOrLength, to) {
    return typeof compareOrFromOrLength === 'function'
        ? arrayElementsAreEqual(first, second, compareOrFromOrLength, fromOrLength, to)
        : arrayElementsAreEqual(first, second, areTheSame, compareOrFromOrLength, fromOrLength);
}
function arrayElementsAreEqual(first, second, elementsAreEqual, fromOrLength, to) {
    let start;
    let end;
    if (to !== undefined) {
        start = fromOrLength ? Math.max(fromOrLength, 0) : 0;
        end = to != null ? to : Math.max(first.length, second.length);
    }
    else if (fromOrLength != null) {
        start = 0;
        end = fromOrLength;
    }
    else {
        start = 0;
        end = Math.max(first.length, second.length);
    }
    if ((first.length <= end || second.length <= end) && first.length !== second.length) {
        return false;
    }
    for (let i = start; i < end; ++i) {
        if (!elementsAreEqual(first[i], second[i], i)) {
            return false;
        }
    }
    return true;
}

/**
 * Builds element or array of the given elements.
 *
 * @param source - A source of elements as their iterable or array-like container.
 *
 * @returns The only element of the `source`, an array of all elements of non-empty source, or `undefined` if the
 * `source` is empty.
 */
function elementOrArray(source) {
    const array = Array.from(source);
    const { length } = array;
    if (length) {
        return length === 1 ? array[0] : array;
    }
    return;
}

/**
 * Builds a set of the given element or array of elements.
 *
 * @param elements Either element, readonly array of elements, `null`, or `undefined`.
 *
 * @returns A new set containing all of the given elements, or empty set if `elements` is `null` or `undefined`.
 */
function setOfElements(elements) {
    return ( /*#__INLINE__*/isArrayOfElements(elements))
        ? new Set(elements)
        : (elements != null ? new Set([elements]) : new Set());
}
/**
 * Adds element of array of elements to the given set.
 *
 * Adds to the set a single element, all `elements` of the given array, or nothing when `elements` is `null`
 * or `undefined`.
 *
 * @param set - Target set to add elements to.
 * @param elements - Element(s) to add to the `set`. Either element, readonly array of elements, `null`, or `undefined`.
 *
 * @returns The target `set`.
 */
function extendSetOfElements(set, elements) {
    if ( /*#__INLINE__*/isArrayOfElements(elements)) {
        elements.forEach(element => set.add(element));
    }
    else if (elements != null) {
        set.add(elements);
    }
    return set;
}

/**
 * Detects a super class of the given class optionally satisfying the given criteria.
 *
 * Traverses all class ancestors until reaches the one satisfying the given criteria, or stops when there is no more
 * ancestors.
 *
 * @param type - The class constructor to find super class of.
 * @param satisfying - The criteria of super class matching. It is a function accepting a super class constructor
 * as the only parameter and returning `true` if the given super class matches, or `false` otherwise. Always returns
 * `true` by default.
 *
 * @return A super class `satisfying` the given criteria, or `undefined` if there is no such super class, or there is no
 * super class at all (e.g. when `Object` is passed in).
 */
function superClassOf(type, satisfying = () => true) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const prototype = Reflect.getPrototypeOf(type.prototype);
    if (prototype == null) {
        return;
    }
    const superType = prototype.constructor;
    if (satisfying(superType)) {
        return superType;
    }
    return superClassOf(superType, satisfying);
}

/**
 * A function that returns its argument as is.
 *
 * @param value - A value to return.
 *
 * @returns `value`.
 */
function asis(value) {
    return value;
}

/**
 * Counts meaningful arguments passed to function.
 *
 * Ignores trailing `undefined` values.
 *
 * @param args - Function call arguments tuple.
 *
 * @returns The number of arguments, except for the trailing `undefined` values.
 */
function countArgs(args) {
    let len = args.length;
    while (len > 0) {
        const idx = len - 1;
        if (args[idx] !== undefined) {
            break;
        }
        len = idx;
    }
    return len;
}

function mergeFunctions(first, second, merge = (_f, s) => s) {
    if (!first) {
        return second;
    }
    if (!second) {
        return first;
    }
    return function (...args) {
        return merge(first.apply(this, args), second.apply(this, args));
    };
}

/**
 * A function that does nothing.
 *
 * @returns `undefined`.
 */
function noop() {
    return;
}

const hasOwnProperty$ = Object.prototype.hasOwnProperty;
/**
 * Checks whether the `target` object has own property with the given `key`.
 *
 * This is a safer variant of `target.hasOwnProperty(key)` call.
 *
 * @param target - Target object to check.
 * @param key - A key of the property to check.
 *
 * @returns `true` if `target` object has own property with the given `key`, or `false` otherwise.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/hasOwnProperty
 */
function hasOwnProperty(target, key) {
    return hasOwnProperty$.call(target, key);
}

/**
 * Checks whether two objects have equal properties.
 *
 * @typeParam - A type of objects to check.
 * @param first - First object to check.
 * @param second - Second object to check.
 * @param valuesAreEqual - A function that compares corresponding property values. Accepts property values from both
 * objects and their key as parameters. Returns `true` if property values are equal. By default, checks property values
 * for {@link areTheSame strict equality}.
 * @param keys - Either an iterable of property keys to compare, or a function returning one. Defaults to own keys
 * (`Reflect.ownKeys`) of both objects.
 *
 * @returns `true` if `valuesAreEqual` returned `true` for each property key, or `false` otherwise.
 */
function propertiesAreEqual(first, second, valuesAreEqual, keys = propertiesAreEqual$ownKeys) {
    valuesAreEqual || (valuesAreEqual = areTheSame);
    const keyList = typeof keys === 'function' ? keys(first, second) : keys;
    for (const key of keyList) {
        const value1 = first[key];
        const value2 = second[key];
        if (!valuesAreEqual(value1, value2, key)) {
            return false;
        }
    }
    return true;
}
function* propertiesAreEqual$ownKeys(first, second) {
    const keys = new Set();
    for (const key of Reflect.ownKeys(first)) {
        yield key;
        keys.add(key);
    }
    for (const key of Reflect.ownKeys(second)) {
        if (!keys.has(key)) {
            yield key;
        }
    }
}

/**
 * Creates a promise resolver.
 *
 * @returns New promise resolver.
 */
function newPromiseResolver() {
    let resolvePromise;
    let rejectPromise;
    let buildPromise = lazyValue(() => new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
    }));
    const settle = (resolution) => {
        buildPromise = lazyValue(resolution);
        resolvePromise = noop;
        rejectPromise = noop;
    };
    resolvePromise = value => {
        settle(() => Promise.resolve(value));
    };
    rejectPromise = error => {
        settle(() => Promise.reject(error));
    };
    return {
        resolve(value) {
            resolvePromise(value);
        },
        reject(reason) {
            rejectPromise(reason);
        },
        promise() {
            return buildPromise();
        },
    };
}

/**
 * Checks whether the given value is array-like.
 *
 * @typeParam TElement - Array elements type.
 * @typeParam TOther - Non-array value type.
 * @param value - A value to check.
 *
 * @returns `true` if the `value` has a `length` property, or `false` otherwise.
 */
function isArrayLike(value) {
    return !!value && typeof value === 'object' && typeof value.length === 'number';
}

/**
 * Checks whether the given object is iterable.
 *
 * @typeParam TElement - Iterable elements type.
 * @typeParam TOther - Non-iterable value type.
 * @param value - An object value to check.
 *
 * @returns `true` if the `value` has a `[Symbol.iterator]` property set, or `false` otherwise.
 */
function isIterable(value) {
    return !!value[Symbol.iterator];
}

/**
 * A function that checks whether the given value is present.
 *
 * @param value - A value to check.
 *
 * @returns `true` when the given `value` is not `null` or `undefined`, or `false` otherwise.
 */
function isPresent(value) {
    return value != null;
}
/**
 * A function that checks whether the given value is not present.
 *
 * This is a direct opposite to `isPresent()`.
 *
 * @param value - A value to check.
 *
 * @returns `true` when the given `value` is `null` or `undefined`, or `false` otherwise.
 */
function isNotPresent(value) {
    return value == null;
}
/**
 * A function that checks whether the given value is defined.
 *
 * @param value - A value to check.
 *
 * @returns `true` when the given `value` is not `undefined`, or `false` otherwise.
 */
function isDefined(value) {
    return value !== undefined;
}
/**
 * A function that checks whether the given value is undefined.
 *
 * This is a direct opposite to `isDefined()`.
 *
 * @param value - A value to check.
 *
 * @returns `true` when the given `value` is `undefined`, or `false` otherwise.
 */
function isUndefined(value) {
    return value === undefined;
}

export { areTheSame, arrayOfElements, arraysAreEqual, asis, asyncByRecipe, asyncRecipe, countArgs, elementOrArray, extendSetOfElements, hasOwnProperty, isArrayLike, isArrayOfElements, isDefined, isIterable, isNotPresent, isPresent, isUndefined, lazyValue, mergeFunctions, newPromiseResolver, noop, propertiesAreEqual, setOfElements, superClassOf, valueByRecipe, valueProvider, valueRecipe, valuesCloner, valuesProvider };
//# sourceMappingURL=primitives.js.map
